import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError } from 'rxjs';
import { ApiService } from './api.service';
import { UserStateService } from './user-state.service';

/**
 * One row of the project picker that backs the "Substituir" action.
 * Backend returns full Project entities (project:read serialization
 * group) — only the fields the picker actually renders are typed here.
 * Extra fields are still present on the wire and accessible via index
 * if needed; this interface just tightens the surface the UI relies on.
 */
export interface CandidateProject {
  id: number;
  originalName?: string;
  name?: string;
  thumbnailUrl?: string;
  lastModificationDate?: string;
}

/**
 * Standard paginated envelope returned by /support/order-items/:id/candidate-projects.
 * `from` and `to` are 1-based inclusive offsets — handy for the
 * "Showing X–Y of N" footer without recomputing on the client.
 */
export interface CandidateProjectsPage {
  items: CandidateProject[];
  pagination: {
    current_page: number;
    limit: number;
    total: number;
    total_pages: number;
    from: number;
    to: number;
  };
}

/**
 * Order item record returned by `GET /support/order-items/:id/project`.
 * Carries the URL of the frozen `.dbproject` in the orders folder along
 * with enough metadata to locate it.
 *
 * `projectInfo` is the linked `Project` record (the customer's current
 * project, NOT the ordered copy). When present, `projectInfo.id` lets
 * the editor fetch the project's metadata for downstream productConfig
 * / editor-type detection — that's why the editor doesn't need a
 * separate `projectId` query param. `projectInfo` is null when the
 * order item has no project linked (e.g. the customer's project was
 * deleted after the order was placed).
 */
export interface SupportOrderItem {
  id: number;
  url: string;
  serverName?: string;
  serverId?: number;
  pathStructureId?: number;
  projectInfo?: { id: number; editorTypeId?: number; productName?: string } | null;
}

/**
 * Support Service - for admin/support operations
 *
 * REST API Methods:
 * - POST /support/login
 * - GET /orders/:orderId/details
 * - GET /support/order-items/:id/project
 * - PUT /support/order-items/:id/project
 * - POST /support/order-items/:id/project
 * - PUT /support/order-items/:id/project/:projectId
 * - GET /support/order-items/:id/candidate-projects
 */
@Injectable({
  providedIn: 'root'
})
export class SupportService {
  private readonly api = inject(ApiService);
  private readonly userState = inject(UserStateService);

  /**
   * Login as another user (support feature)
   * @param email - Email of the user to login as
   * @param invalidateSession - 1 to invalidate session (clear vouchers), 0 otherwise
   * @returns UserInfo of the target user
   */
  supportLogin(email: string, invalidateSession: boolean = false): Observable<any> {
    const user = this.userState.user();
    if (!user) return of(null);

    return this.api.post('/support/login', { username: email, adminUserId: user.id, invalidateSession: invalidateSession ? 1 : 0 }).pipe(
      catchError(err => {
        console.error('[SupportService] supportLogin error:', err);
        return of(null);
      })
    );
  }

  /**
   * Impersonation shortcut: log in as the customer that owns `orderId`.
   * Same response shape as POST /support/login — feed it straight into
   * UserService.handleSupportLogin to park admin tokens, swap to the
   * customer's, and start the support session.
   *
   * The endpoint deliberately accepts disabled / soft-deleted customers
   * so support can audit historical orders; the caller is responsible
   * for surfacing a warning when `user.enabled === false`.
   *
   * 404 ORDER_OR_CUSTOMER_NOT_FOUND when the order doesn't exist or has
   * no linked user — let the HttpErrorResponse bubble so the caller can
   * map it (no catchError here, on purpose).
   */
  loginByOrder(orderId: number): Observable<any> {
    return this.api.post(`/support/login/by-order/${orderId}`, {});
  }

  // ── Order-item project repair (admin-context, gated by staffGuard) ──
  //
  // The endpoints below intentionally DO NOT catchError — the caller needs
  // the original HttpErrorResponse to feed into supportErrorKey(), which
  // maps backend `error` codes (PROJECT_CUSTOMER_MISMATCH, BACKUP_FAILED,
  // etc.) to translation keys. Swallowing here would lose the discriminator.

/**
   * Fetch the order item record for support-edit mode. Returns an
   * OrderItem with `url` pointing at the frozen `.dbproject` in the
   * orders folder; the editor then fetches the file content from that
   * URL. Calling via the service means a refresh on `?supportOrderItemId=X`
   * just re-runs this lookup — no Router state needed.
   */
  getOrderItemProject(orderItemId: number): Observable<SupportOrderItem> {
    return this.api.get<SupportOrderItem>(`/support/order-items/${orderItemId}/project`);
  }

  /**
   * Re-sync the order item's order file from the customer's *current*
   * project file. Use when the customer edited the project after ordering
   * and the order's frozen copy needs to catch up. Backend backs up the
   * old order file before overwriting.
   *
   * Surfaced as "Atualizar" in the per-item repair menu.
   */
  syncOrderItemProject(orderItemId: number): Observable<void> {
    return this.api.put<void>(`/support/order-items/${orderItemId}/project`);
  }

  /**
   * Replace the order item's project file with a JSON payload. Used by
   * the editor's support-edit save path (PR 8) — saves the agent's
   * patched version of the ordered .dbproject back to the orders folder.
   * The size cap is enforced server-side at 10 MB (MAX_PROJECT_JSON_BYTES).
   *
   * NOT exposed in the menu; called from the editor when it loads via
   * `?supportOrderItemId=X`.
   */
  replaceOrderItemProject(orderItemId: number, projectJson: string): Observable<void> {
    return this.api.post<void>(
      `/support/order-items/${orderItemId}/project`,
      { project: projectJson }
    );
  }

  /**
   * Swap the order item to point at a different project (same customer,
   * same product). Server validates the customer/product match and
   * returns PROJECT_CUSTOMER_MISMATCH / PROJECT_PRODUCT_MISMATCH on
   * conflict. The picker pre-filters via listCandidateProjects so a
   * mismatch is unlikely — but the server check is the safety net.
   *
   * Surfaced as "Substituir" in the per-item repair menu.
   */
  swapOrderItemProject(orderItemId: number, projectId: number): Observable<void> {
    return this.api.put<void>(`/support/order-items/${orderItemId}/project/${projectId}`);
  }

  /**
   * List candidate projects the agent can swap into for this order item.
   * Server-side filter mirrors swapToProject's validation:
   *   - same customer as the order
   *   - same product as the order item
   *   - deleted=0 and saved=1
   *   - excludes the project currently linked
   *   - ordered by lastModificationDate DESC
   *
   * Optional `q` does a LIKE match on originalName (≥1 char). `limit`
   * defaults to 50 server-side, capped at 100. `page` is 1-based.
   *
   * Errors surfaced as 404 ORDER_ITEM_NOT_FOUND or 422
   * ORDER_ITEM_MISSING_CONTEXT — both mapped by supportErrorKey().
   */
  listCandidateProjects(
    orderItemId: number,
    q?: string,
    limit?: number,
    page?: number
  ): Observable<CandidateProjectsPage> {
    const params: Record<string, string | number> = {};
    if (q && q.length > 0) params['q'] = q;
    if (limit !== undefined) params['limit'] = limit;
    if (page !== undefined) params['page'] = page;
    return this.api.get<CandidateProjectsPage>(
      `/support/order-items/${orderItemId}/candidate-projects`,
      { params }
    );
  }
}
